import React, {
  FunctionComponent,
  useContext,
  useEffect,
  useState,
} from "react";
import backArrowIcon from "../../../assets/images/back_arrow.svg";
import HeadingText from "../../common/HeadingText/HeadingText";
import FormSectionLayout from "../../layouts/FormSectionLayout";
import { Col, Container, Form, Label, Row } from "reactstrap";
import {
  ICategoryCityLevelPayload,
  ICategoryProductsResponse,
  ICategoryProductUpdatePayload,
  IIdValuePairResponse,
  IImageObject,
  IUpdateCategoryCityLevelProps,
} from "../../../interfaces/Index";
import ButtonComponent from "../../common/Button/ButtonComponent";
import LoaderComponent from "../../common/Loader/Loader";
import { AbilityContext, Can } from "../../../config/Can";
import "./UpdateCategoryCityLevelPage.scss";
import Switch from "../../common/Switch/Switch";
import RadioButton from "../../common/RadioButton/RadioButton";
import { categoryFlags } from "./CategoryCityLevelConstants";
import SortableList from "../../common/SortableList/SortableList";
import {
  normalizeCategoryImageData,
  normalizeCategoryProductReqPayload,
} from "../../../normalizer/CategoryNormalizer";
import Toaster from "../../common/Toaster/Toaster";
import UploadButtonComponent from "../../common/UploadButton/UploadButtonComponent";
import { uploadImageApiCall } from "utils/UploadImage";
import { acceptableFormats } from "config/constants";

const UpdateCategoryCityLevelPage: FunctionComponent<
  IUpdateCategoryCityLevelProps
> = (props: IUpdateCategoryCityLevelProps) => {
  const ability = useContext(AbilityContext);
  const [pageState, setPageState] = useState<PageState>({
    categoryId: props.category.id,
    cityId: props.city.id,
    flag: props.category.categoryFlag,
    isEnabled: props.category.isEnabled,
    imageId: props.category.categoryImageId,
    image: props.category.categoryImage,
    categoryProductList: props.categoryProductList,
    parent: {
      id: props.category.parentId || "",
      name: props.categoryParent?.categoryTitle || "",
    },
  });
  const [showToaster, setToaster] = useState({
    isVisible: false,
    message: "",
    isError: false,
  });

  const { categoryHandle, categoryTitle } = props.category;
  const showCategoryProductDetails =
    pageState.categoryProductList && pageState.categoryProductList.length > 0;
  const showParentCategoryDetails = !!props.categoryParent;

  useEffect(() => {
    props.fetchCategoryProducts(pageState.categoryId, pageState.cityId);
  }, []);
  useEffect(() => {
    updateState("categoryProductList", props.categoryProductList);
  }, [props.categoryProductList]);
  useEffect(() => {
    props.isCategoryUpdated && props.backButtonHandler(false);
  }, [props.isCategoryUpdated]);
  useEffect(() => {
    const { isError, error } = props;
    if (isError && error) {
      alert((error as any).errors);
      handleToaster(true, (error! as any).errors);
    }
  }, [props.isError]);

  const handleToaster = (error: boolean, message: string) => {
    setToaster({
      isVisible: true,
      message: message,
      isError: error,
    });
  };
  const updateState = (fieldKey: string, fieldValue: any) => {
    setPageState({
      ...pageState,
      [fieldKey]: fieldValue,
    });
  };
  const handleChange = (e: any) => {
    const fieldKey: string = e.target.name;
    let fieldValue: any = e.target.value;
    fieldValue = fieldKey === "isEnabled" ? !pageState[fieldKey] : fieldValue;
    updateState(fieldKey, fieldValue);
  };
  const handleSubmit = () => {
    if (pageState.categoryProductList && pageState.categoryProductList.length) {
      const categoryProductsReqPayload: ICategoryProductUpdatePayload =
        normalizeCategoryProductReqPayload(pageState.categoryProductList);
      props.reorderCategoryProducts(
        pageState.categoryId,
        pageState.cityId,
        categoryProductsReqPayload
      );
    }

    const categoryReqPayload: ICategoryCityLevelPayload = {
      categoryFlag: pageState.flag,
      isEnabled: pageState.isEnabled,
      imageId: pageState.imageId,
    };
    if ((pageState.image as IImageObject).multiPartData) {
      uploadImageApiCall(
        "category",
        normalizeCategoryImageData(pageState.image as IImageObject)
      ).then((data: any) => {
        categoryReqPayload.imageId = data.id || "";
        props.updateCategory(
          pageState.categoryId,
          pageState.cityId,
          categoryReqPayload
        );
      });
      return;
    }

    props.updateCategory(
      pageState.categoryId,
      pageState.cityId,
      categoryReqPayload
    );
  };

  const uploadImage = () => {
    const imgSrc: string = (pageState.image as IImageObject).base64StringUrl
      ? (pageState.image as IImageObject).base64StringUrl
      : (pageState.image as string);
    return (
      <div className="category-image-upload-container">
        <div className="category-image-container">
          <img src={imgSrc} alt="category" />
        </div>
        <Can I="create" a="image">
          <UploadButtonComponent
            id="categoryImage"
            helperText="400*400 (max 200kb)"
            buttonText="Upload image"
            fileFormat={acceptableFormats.image}
            handleFileChange={(payload: any) => updateState("image", payload)}
          />
        </Can>
      </div>
    );
  };
  const categoryGlobalDetails = () => {
    return (
      <FormSectionLayout subTitleText="" leftColumnChildren={uploadImage()}>
        <div className="category-basic-details">
          <div className="details">
            <Label className="label">{categoryHandle}</Label>
            <HeadingText isMainHeading={false} text={categoryTitle} />
            <Label className="label">
              {pageState.categoryProductList.length} items
              {showParentCategoryDetails && " . " + pageState.parent.name}
            </Label>
          </div>
        </div>
      </FormSectionLayout>
    );
  };

  const categoryCityLevelDetails = () => {
    return (
      <FormSectionLayout subTitleText="Category details">
        <RadioButton
          radioButtonArray={packageFlags(pageState.flag)}
          label="Category Flags"
          onChange={handleChange}
        />
        <Switch
          id="isEnabled"
          name="isEnabled"
          label="Display on App"
          checked={pageState.isEnabled}
          handleSwitch={handleChange}
        />
      </FormSectionLayout>
    );
  };

  const renderFormButton = () => {
    return (
      <div className="form-row-separator">
        <Row id="addEditButtonRow">
          <Col lg="6">
            {ability.can("update", "category") && (
              <ButtonComponent
                buttonClass="large-button btn-primary"
                buttonText="Update Category"
                onClickHandler={handleSubmit}
              />
            )}
          </Col>
        </Row>
      </div>
    );
  };

  // const categoryImageDetails = () => {
  //   const imgSrc: string = (pageState.image as IImageObject).base64StringUrl
  //     ? (pageState.image as IImageObject).base64StringUrl
  //     : (pageState.image as string);
  //   return (
  //     <FormSectionLayout subTitleText="Category Image">
  //       <div className="category-image-upload-container">
  //         <div className="category-image-container">
  //           <img src={imgSrc} alt="category" />
  //         </div>
  //         <Can I="create" a="image">
  //           <UploadButtonComponent
  //             id="categoryImage"
  //             helperText="400*400 - max 200kb"
  //             buttonText="Upload .png image"
  //             handleFileChange={(payload: any) => updateState("image", payload)}
  //           />
  //         </Can>
  //       </div>
  //     </FormSectionLayout>
  //   );
  // };

  const categoryProductSequence = () => {
    return (
      showCategoryProductDetails && (
        <FormSectionLayout subTitleText="Product sequence">
          <div className="product-sequence-container">
            <SortableList
              categoryProducts={pageState.categoryProductList}
              handleProductSortEnd={(productOrdering: any) =>
                updateState("categoryProductList", productOrdering)
              }
            />
          </div>
        </FormSectionLayout>
      )
    );
  };

  return (
    <Container fluid>
      {props.isLoading && <LoaderComponent />}
      <Toaster
        showToaster={showToaster.isVisible}
        toasterClass={
          showToaster.isError
            ? "toaster toaster-bg-danger"
            : "toaster toaster-bg-success"
        }
      >
        {showToaster.message || ""}
      </Toaster>
      <Row>
        <Col>
          <div className="back-button">
            <a
              href="javascript:void(0)"
              onClick={() => props.backButtonHandler()}
            >
              {" "}
              <span>
                <img src={backArrowIcon} alt="close" />
              </span>{" "}
              Back to Categories
            </a>
          </div>
          <HeadingText text="Category details" isMainHeading={true} />
        </Col>
      </Row>
      <Row className="form-container">
        <Col xs="12">
          <Form>
            <div className="form-row-separator">{categoryGlobalDetails()}</div>
            <div className="category-detail">{categoryCityLevelDetails()}</div>
            {/* <div className="form-row-separator">{categoryImageDetails()}</div> */}
            <div className="form-row-separator">
              {categoryProductSequence()}
            </div>
            <FormSectionLayout subTitleText="">
              {renderFormButton()}
            </FormSectionLayout>
          </Form>
        </Col>
      </Row>
    </Container>
  );
};

const packageFlags = (selectedFlagValue: string) => {
  const flags: any = [];
  Object.keys(categoryFlags).forEach((flagValue) => {
    flags.push(
      Object.assign({}, categoryFlags[flagValue], {
        name: "flag",
        value: flagValue,
        checked: flagValue === selectedFlagValue,
      })
    );
  });
  return flags;
};

export default UpdateCategoryCityLevelPage;

export interface PageState {
  categoryId: string;
  cityId: string;
  flag: string;
  isEnabled: boolean;
  imageId: string;
  image: string | IImageObject;
  categoryProductList: Array<ICategoryProductsResponse>;
  parent: IIdValuePairResponse;
}
